import React, { useEffect, useMemo, useState } from "react";
import {
  ThemeProvider,
  CssBaseline,
  createTheme,
  Box,
  useMediaQuery,
  Fab,
  Zoom,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { getDesignTokens } from "./theme";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { SnackbarProvider } from "./context/snakbarProvider";
import Navbar from "./components/Navbar";
import Home from "./screens/Home";
import ServicesList from "./components/ServicesList";
import InteractiveMascot from "./components/InteractiveMascot";
import PromoVideo from "./components/PromoVideo";
import CrewList from "./components/CrewList";
import JoinUs from "./components/JoinUs";
import MissionVision from "./components/MissionVision";
import AboutUs from "./components/AboutUs";
import ContactFooter from "./components/ContactUs";
import AnimatedBackground from "./components/AnimatedBackground";
import DashboardLayout from "./admin/drawer";
import AboutUsForm from "./admin/components/abouttab";
import Login from "./admin/Login";
import ServicesAdminScreen from "./admin/components/servicesAdmintab";
import CrewAdminScreen from "./admin/components/crewAdminList";
import MissionVisionForm from "./admin/components/mandv";
import RequireAuth from "./utils/requireAuth";
import RequestsAdminScreen from "./admin/components/requests";
import BannersAdminScreen from "./admin/components/BannersAdminScreen";
import ContactUsAdmin from "./admin/components/contactUsAdmin";
import ModifyUserScreen from "./admin/users/modifyAdmins";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { getBrands } from "./services/api/getAll";
import BrandsAdminScreen from "./admin/components/brandsAdmin";
import BookingsAdminScreen from "./admin/components/bookings";
import PromoPricingAdminScreen from "./admin/components/promopricing";

const queryClient = new QueryClient();

const AppContent = () => {
  const location = useLocation();
  const [brands, setBrands] = useState([]);
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await getBrands();
        setBrands(response);
      } catch (error) {
        console.error("Failed to fetch brands:", error);
      }
    };

    fetchBrands();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const isDashboardSubdomain =
    window.location.hostname.startsWith("dashboard.") ||
    window.location.hostname.includes("dashboard") ||
    window.location.pathname.startsWith("/dashboard") ||
    window.location.pathname.startsWith("/admin") ||
    !!(window as any).Capacitor;

  if (isDashboardSubdomain) {
    return (
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/dashboard/login" element={<Login />} />
        <Route element={<RequireAuth />}>
          <Route path="/dashboard/*" element={<DashboardLayout />}>
            <Route path="BookingsAdminScreen" element={<BookingsAdminScreen />} />
            <Route path="PromoPricingAdminScreen" element={<PromoPricingAdminScreen />} />
            <Route path="about" element={<AboutUsForm />} />
            <Route
              path="ServicesAdminScreen"
              element={<ServicesAdminScreen />}
            />
            <Route path="BrandsAdminScreen" element={<BrandsAdminScreen />} />
            <Route path="CrewAdminScreen" element={<CrewAdminScreen />} />
            <Route path="MissionVisionForm" element={<MissionVisionForm />} />
            <Route path="ContactUsAdmin" element={<ContactUsAdmin />} />
            <Route
              path="RequestsAdminScreen"
              element={<RequestsAdminScreen />}
            />
            <Route path="BannersAdminScreen" element={<BannersAdminScreen />} />
            <Route path="ModifyUserScreen" element={<ModifyUserScreen />} />
            <Route index element={<Navigate to="BookingsAdminScreen" replace />} />
            <Route path="*" element={<div>Not Found</div>} />
          </Route>
        </Route>
      </Routes>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: !isMobile ? "100%" : "100vw",
        position: "relative",
        background: "#080808",
        color: "#ffffff",
        minHeight: "100vh",
      }}
    >
      {/* Global Background Grid Mesh */}
      <Box
        className="bg-mesh-overlay"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* Dynamic Animated Constellation Particle Background */}
      <AnimatedBackground />

      {/* Global Ambient Glow Blobs */}
      <Box
        sx={{
          position: "fixed",
          width: { xs: "350px", md: "700px" },
          height: { xs: "350px", md: "700px" },
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.05) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(90px)",
          top: "10%",
          left: "-10%",
          zIndex: 0,
          pointerEvents: "none",
          animation: "bg-glow-float 20s ease-in-out infinite alternate",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          width: { xs: "400px", md: "800px" },
          height: { xs: "400px", md: "800px" },
          background: "radial-gradient(circle, rgba(255, 174, 0, 0.035) 0%, rgba(255, 174, 0, 0) 70%)",
          filter: "blur(100px)",
          bottom: "10%",
          right: "-15%",
          zIndex: 0,
          pointerEvents: "none",
          animation: "bg-glow-float 25s ease-in-out infinite alternate-reverse",
        }}
      />

      <Navbar />
      <section id="home" style={{ position: "relative", zIndex: 1 }}>
        <Home Brands={brands[brands?.length - 1]} />
      </section>
      <section id="promo" style={{ position: "relative", zIndex: 1 }}>
        <PromoVideo />
      </section>
      <section id="services" style={{ position: "relative", zIndex: 1 }}>
        <ServicesList />
      </section>
      <section id="about" style={{ position: "relative", zIndex: 1 }}>
        <AboutUs />
      </section>
      <section id="mission-vision" style={{ position: "relative", zIndex: 1 }}>
        <MissionVision />
      </section>
      <section id="crew" style={{ position: "relative", zIndex: 1 }}>
        <CrewList />
      </section>
      <section id="join" style={{ position: "relative", zIndex: 1 }}>
        <JoinUs />
      </section>
      <section id="contact" style={{ position: "relative", zIndex: 1 }}>
        <ContactFooter Brands={brands[brands?.length - 1]} />
      </section>

      {/* Scroll to Top Button */}
      <Zoom in={showScrollTop}>
        <Fab
          onClick={scrollToTop}
          size="medium"
          aria-label="scroll back to top"
          sx={{
            position: "fixed",
            bottom: { xs: 20, md: 30 },
            [i18n.language.startsWith("ar") ? "left" : "right"]: { xs: 20, md: 30 },
            zIndex: 1000,
            background: "linear-gradient(135deg, #FFAE00 0%, #FF8F00 100%)",
            color: "#000",
            boxShadow: "0 4px 20px rgba(255, 174, 0, 0.3)",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              background: "linear-gradient(135deg, #ffc107 0%, #FFAE00 100%)",
              boxShadow: "0 8px 30px rgba(255, 174, 0, 0.5)",
              transform: "translateY(-4px)",
            },
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Zoom>

      {/* Interactive Mascot Assistant */}
      <InteractiveMascot />
    </Box>
  );
};

const App = () => {
  const [mode, setMode] = useState<"light" | "dark">("dark");
  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  useEffect(() => {
    document.documentElement.style.scrollPaddingTop = "120px";
    document.body.style.overflowX = "clip";
    document.documentElement.style.overflowX = "visible";

    const meta = document.createElement("meta");
    meta.name = "viewport";
    meta.content = "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no";
    document.head.appendChild(meta);
  }, []);

  return (
    <I18nextProvider i18n={i18n}>
      <SnackbarProvider>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Router>
              <AppContent />
            </Router>
          </ThemeProvider>
        </QueryClientProvider>
      </SnackbarProvider>
    </I18nextProvider>
  );
};

export default App;
